import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DocsContent } from '../components/DocsContent';
import { DocsTree } from '../components/DocsTree';
import { DocContent, DocNode } from '../models/types';
import { DocsIndexService } from '../services/docsIndexService';

const Docs = (): JSX.Element => {
  const [tree, setTree] = useState<DocNode[]>([]);
  const [selectedPath, setSelectedPath] = useState<string>();
  const [doc, setDoc] = useState<DocContent>();
  const [error, setError] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const load = async (): Promise<void> => {
      const index = await DocsIndexService.fetchTree();
      setTree(index);

      const requested = searchParams.get('path');
      if (requested) {
        setSelectedPath(requested);
        return;
      }

      const pickFirst = (nodes: DocNode[]): DocNode | undefined => {
        for (const node of nodes) {
          if (node.type === 'file') {
            return node;
          }
          if (node.children) {
            const child = pickFirst(node.children);
            if (child) {
              return child;
            }
          }
        }
        return undefined;
      };

      const firstFile = pickFirst(index);
      if (firstFile) {
        setSelectedPath(firstFile.path);
        setSearchParams({ path: firstFile.path });
      }
    };

    void load();
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedPath) {
      return;
    }

    const loadContent = async (): Promise<void> => {
      setError(undefined);
      try {
        const content = await DocsIndexService.fetchContent(selectedPath);
        setDoc(content);
        setSearchParams({ path: selectedPath });
      } catch (err) {
        setError('Unable to load document. Source stays read-only; try another file.');
        console.error(err);
      }
    };

    void loadContent();
  }, [selectedPath, setSearchParams]);

  return (
    <div>
      <h1 className="section-title">Documentation</h1>
      <p className="muted">
        Read-only viewer over existing markdown. Files stay where they are: Platform, Product,
        Infrastructure, and Legacy folders are surfaced without renaming or moving content.
      </p>

      {error && <div className="pill warning">{error}</div>}

      <div className="docs-layout" style={{ marginTop: '12px' }}>
        <div className="docs-panel">
          <strong>Sources</strong>
          <p className="muted" style={{ marginTop: '4px' }}>
            /docs, /platform/docs, /platform/infra, /old/docs, /root/docs
          </p>
          <DocsTree nodes={tree} onSelect={setSelectedPath} selectedPath={selectedPath} />
        </div>
        <DocsContent doc={doc} />
      </div>
    </div>
  );
};

export default Docs;
